import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "../components/shared/Primitives";
import { CreditCard, AlertTriangle, CheckCircle2, Clock, ClipboardList } from "lucide-react";
import { api, formatApiError } from "../lib/api";

// Native Account & Billing — reached only via the magic-link handover
// session (wrapped in <HandoverGate> with no requiredEntitlement, i.e.
// "any active member"), NOT the legacy JWT AuthContext.
const STATUS_BADGE = {
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
  trialing: { label: "Free trial", cls: "bg-purple/10 text-purple" },
  past_due: { label: "Payment issue", cls: "bg-amber-100 text-amber-700" },
  canceled: { label: "Cancelled", cls: "bg-slate-100 text-slate-500" },
  inactive: { label: "Inactive", cls: "bg-slate-100 text-slate-500" },
};

const SERVICE_STATUS_LABELS = {
  awaiting_questionnaire: "Complete your questionnaire",
  submitted: "Questionnaire submitted",
  in_progress: "In progress",
  awaiting_approval: "Awaiting your approval",
  approved: "Approved",
  changes_requested: "Changes requested",
  completed: "Completed",
};

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return null;
  }
}

export default function AccountBilling() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/api/membership/my-subscriptions");
        setData(d);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    })();
    (async () => {
      try {
        const { data: d } = await api.get("/api/shop/my-orders");
        setOrders(d.orders || []);
      } catch {
        setOrders([]);
      }
    })();
  }, []);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data: d } = await api.post("/api/membership/portal", { return_url: window.location.href });
      window.location.assign(d.url);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setPortalLoading(false);
    }
  };

  return (
    <div data-testid="page-account-billing">
      <PageHero eyebrow="Your Account" title="Account & billing" description="See your current membership, trial and billing status in one place." />
      <section className="section">
        <div className="container-jcc max-w-2xl space-y-6">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex gap-2" data-testid="account-billing-error">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          {!data && !error && (
            <p className="text-ink-muted" data-testid="account-billing-loading">Loading your membership…</p>
          )}

          {data && (
            <>
              <div className="card-base p-6" data-testid="account-billing-entitlements">
                <p className="text-sm font-semibold text-ink mb-2">Current access</p>
                <div className="flex flex-wrap gap-2">
                  {(data.entitlements || []).length === 0 && (
                    <span className="text-sm text-ink-muted">No active JCC access yet.</span>
                  )}
                  {(data.entitlements || []).map((e) => (
                    <span key={e} className="pill bg-slate-100 text-ink-muted text-xs" data-testid={`entitlement-pill-${e}`}>
                      {e.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                {data.subscriptions.length === 0 && (
                  <div className="card-base p-6 text-center text-ink-muted" data-testid="account-billing-no-subscriptions">
                    You don't have any paid Stripe subscriptions on this account yet.
                    <div className="mt-4 flex flex-wrap justify-center gap-3">
                      <Link to="/membership/pro-carer-circle" className="btn-purple">Explore Pro Carer Office</Link>
                      <Link to="/membership/family-circle" className="btn-teal">Explore Family Care Office</Link>
                    </div>
                  </div>
                )}
                {data.subscriptions.map((s) => {
                  const badge = STATUS_BADGE[s.status] || STATUS_BADGE.inactive;
                  return (
                    <div key={s.plan_id} className="card-base p-6 flex items-start justify-between gap-4" data-testid={`subscription-card-${s.plan_id}`}>
                      <div>
                        <p className="font-semibold text-ink">{s.plan_label}</p>
                        <span className={`inline-block mt-1 pill text-xs ${badge.cls}`} data-testid={`subscription-status-${s.plan_id}`}>{badge.label}</span>
                        {s.status === "trialing" && (
                          <p className="mt-2 text-sm text-ink-muted flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Your free trial is active.</p>
                        )}
                        {s.payment_issue && (
                          <p className="mt-2 text-sm text-amber-700 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Your last payment failed — please update your card.</p>
                        )}
                        {!s.payment_issue && s.status === "active" && formatDate(s.current_period_end) && (
                          <p className="mt-2 text-sm text-ink-muted flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Next billing date: {formatDate(s.current_period_end)}</p>
                        )}
                        {s.status === "canceled" && (
                          <p className="mt-2 text-sm text-ink-muted">You'll keep any lower-tier free access this membership included.</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {data.subscriptions.some((s) => s.has_billing_portal) && (
                <button onClick={openBillingPortal} disabled={portalLoading} className="btn-outline inline-flex items-center gap-2" data-testid="account-billing-portal-button">
                  <CreditCard className="w-4 h-4" /> {portalLoading ? "Opening billing portal…" : "Manage billing & cancel"}
                </button>
              )}

              {orders && orders.length > 0 && (
                <div className="card-base p-6" data-testid="account-billing-service-orders">
                  <p className="text-sm font-semibold text-ink mb-3">My service orders</p>
                  <div className="space-y-3">
                    {orders.map((o) => (
                      <div key={o.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`service-order-${o.id}`}>
                        <div>
                          <p className="font-medium text-ink">{o.product_title}</p>
                          <p className="text-ink-muted">{SERVICE_STATUS_LABELS[o.service_status] || o.service_status} · {o.price_display}</p>
                        </div>
                        {["awaiting_questionnaire", "submitted"].includes(o.service_status) && o.questionnaire_token && (
                          <Link to={`/shop/questionnaire/${o.questionnaire_token}`} className="btn-outline text-xs shrink-0" data-testid={`service-order-questionnaire-link-${o.id}`}>
                            <ClipboardList className="w-3.5 h-3.5" /> {o.service_status === "submitted" ? "Edit" : "Complete"} questionnaire
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
